// Blog post registry. Markdown bodies are imported as raw strings (Vite ?raw).
// Lazy-loaded by the post detail page; index page only needs metadata.
import pharmacistOversightMd from "./pharmacist-oversight-snf-beyond-quarterly-checklist.md?raw";
import hipaaAuditMd from "./hipaa-grade-ai-synthesis-what-an-audit-trail-looks-like.md?raw";
import stoppStartBeersMd from "./stopp-start-beers-geriatric-medication-review-at-scale.md?raw";
import waterQualityMd from "./water-quality-pharmacy-oversight-dialysis.md?raw";
import multiTenancyMd from "./tenant-scoped-multi-tenancy-clinical-saas.md?raw";

export interface BlogPostMeta {
  slug: string;
  title: string;
  description: string; // ≤ 155 chars, used for meta description + index card excerpt
  date: string; // ISO yyyy-mm-dd, publish date
  author: string;
  readingTimeMin: number;
  audience: string;
  tags: string[];
}

export interface BlogPost extends BlogPostMeta {
  body: string; // markdown source
}

// Newest first. Sort is explicit, not date-derived, so launch ordering is stable.
export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "tenant-scoped-multi-tenancy-clinical-saas",
    title: "The case for tenant-scoped multi-tenancy in clinical SaaS",
    description:
      "Why row-level security at the database — not application-layer filtering — is the only defensible multi-tenant model for clinical SaaS.",
    date: "2026-05-30",
    author: "WeConsult Rx Team",
    readingTimeMin: 7,
    audience: "Security reviewers · IT directors · Technical buyers",
    tags: ["Security", "Architecture", "HIPAA"],
    body: multiTenancyMd,
  },
  {
    slug: "water-quality-pharmacy-oversight-dialysis",
    title: "Water quality and pharmacy oversight: the hidden risk in dialysis pharmacy",
    description:
      "Dialysate quality alters the pharmacology of every drug a hemodialysis patient receives. Why integrated oversight beats two-silo workflows.",
    date: "2026-05-30",
    author: "WeConsult Rx Team",
    readingTimeMin: 6,
    audience: "Dialysis facility administrators · Medical directors",
    tags: ["Dialysis", "ESRD", "AAMI"],
    body: waterQualityMd,
  },
  {
    slug: "stopp-start-beers-geriatric-medication-review-at-scale",
    title: "STOPP/START + Beers: making geriatric medication review tractable at scale",
    description:
      "Encoded rule packs flag candidate interventions; the consultant pharmacist still decides. How an edit-pattern learning loop tightens rules over time.",
    date: "2026-05-30",
    author: "WeConsult Rx Team",
    readingTimeMin: 7,
    audience: "Consultant pharmacists · Clinical pharmacy directors",
    tags: ["Clinical", "Geriatric", "STOPP/START", "Beers"],
    body: stoppStartBeersMd,
  },
  {
    slug: "hipaa-grade-ai-synthesis-what-an-audit-trail-looks-like",
    title: "HIPAA-grade AI synthesis: what an audit trail actually looks like",
    description:
      "Trust is not a logo. A real audit log shows action, actor, resource, prompt hash, citations, edit diffs — and the BAA chain to back it up.",
    date: "2026-05-30",
    author: "WeConsult Rx Team",
    readingTimeMin: 8,
    audience: "Compliance officers · IT directors",
    tags: ["HIPAA", "Audit", "AI", "BAA"],
    body: hipaaAuditMd,
  },
  {
    slug: "pharmacist-oversight-snf-beyond-quarterly-checklist",
    title: "Why pharmacist oversight in SNFs needs more than a quarterly checklist",
    description:
      "CMS Phase 3 expects continuous clinical partnership, not point-in-time review. How AI-assisted chart review extends the consultant pharmacist's reach.",
    date: "2026-05-30",
    author: "WeConsult Rx Team",
    readingTimeMin: 6,
    audience: "SNF DONs · Administrators · Consultant pharmacists",
    tags: ["SNF", "CMS Phase 3", "Workflow"],
    body: pharmacistOversightMd,
  },
];

export function getPostBySlug(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((p) => p.slug === slug);
}

export function listPostsSorted(): BlogPost[] {
  // Stable order: explicit registry order (already newest-first), tie-break by slug for determinism.
  return [...BLOG_POSTS].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? 1 : -1;
    return a.slug.localeCompare(b.slug);
  });
}
